import type { MouseEvent } from "react";
import { useEffect } from "react";
import { styled } from "styled-components";

import { bookmarksApiSlice } from "api/bookmarksApi.slice";
import type { EmergingTheme } from "api/schemas/emergingThemesApiSchemas";
import {
  Image,
  JoinUsUpgradeBanner,
  MasterDetailContentWrapper,
  SectionTitleUnderlined,
  UIActionButton,
  UIButtonsRow,
} from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import type { TopicBackLabelData } from "context/session/sessionTypes";
import { getUserId } from "domains/authentication/selectors";
import { fetchClusterById } from "domains/clusters/actions";
import { ClusterCaption } from "domains/clusters/components/ClusterCaption";
import { ClusterSources } from "domains/clusters/components/ClusterSources";
import { DigDeeperList } from "domains/clusters/components/DigDeeperList";
import type { Cluster } from "domains/clusters/types";
import { getSiteLanguage } from "domains/language/selectors";
import { shareLinkAction } from "domains/share/actions";
import { TopicSlider } from "domains/topic/components/TopicSlider";
import { getCanAccessMonitor, getCanDisplayBookmarks } from "entities/permissions/selectors";

const StyledImage = styled(Image)`
  width: 100%;
  height: 250px;
  border-radius: 5px;
  margin-bottom: 1rem;
  box-shadow: ${({ theme }) => theme.shadows.box};
`;

const StyledTitle = styled.h1`
  font-size: 2rem;
  margin: 0;
`;

const StyledSummary = styled.p`
  margin-top: 1rem;
  font-size: 1.1rem;
  line-height: 1.75rem;
  white-space: pre-line;
`;

const StyledDigDeeperList = styled.div`
  flex: 1;
`;

const StyledButtons = styled(UIButtonsRow)`
  margin: 1rem 0;
`;

const StyledDigDeeperSection = styled(MasterDetailContentWrapper)`
  margin-top: 2rem;
`;

type ClusterDetailProps = { cluster?: Cluster | EmergingTheme; id: string; topicBack: TopicBackLabelData };

export const ClusterDetail = ({ cluster, id, topicBack }: ClusterDetailProps) => {
  const track = useTrack();
  const dispatch = useDispatch();
  const hasAccessToBookmarks = useSelector(getCanDisplayBookmarks);
  const hasAccessToMonitor = useSelector(getCanAccessMonitor);
  const language = useSelector(getSiteLanguage);
  const userId = useSelector(getUserId);
  const translate = useTranslate();

  const [addKnowledgeToBookmarks, { isLoading: isAddBookmarkLoading }] =
    bookmarksApiSlice.endpoints.addKnowledgeToBookmarks.useMutation();
  const [removeKnowledgeFromBookmarks, { isLoading: isRemoveBookmarkLoading }] =
    bookmarksApiSlice.endpoints.removeKnowledgeFromBookmarks.useMutation();

  const isBookmarkPending = isAddBookmarkLoading || isRemoveBookmarkLoading;

  useEffect(() => {
    dispatch(fetchClusterById(id));
  }, [dispatch, id, language, userId]);

  if (!cluster) {
    return null;
  }

  const {
    title,
    description,
    time,
    knowledge_count,
    images,
    sources_count,
    days_count,
    sources,
    related_topics,
    is_bookmarked,
  } = cluster;

  const onBookmarkHandle = async (event?: MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if ("is_bookmarked" in cluster && cluster.is_bookmarked) {
      await removeKnowledgeFromBookmarks({ id: cluster.id });
    } else {
      await addKnowledgeToBookmarks({ id: cluster.id });
    }

    track({
      entityId: id,
      entityName: cluster.title,
      entityType: "Cluster",
      name: "Bookmark",
    });
  };

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}/monitor/emerging-themes/${id}`;

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id,
        name: title,
        link: shareLink ?? "",
        type: "Cluster",
      }),
    );

  return (
    <>
      <MasterDetailContentWrapper>
        <StyledImage src={images?.large} alt={title} />
        <ClusterCaption time={time} knowledgeCount={knowledge_count} />
        <StyledTitle>{title}</StyledTitle>
        <StyledSummary>{description}</StyledSummary>
        <ClusterSources sourcesCount={sources_count} daysCount={days_count} sources={sources} />
        <StyledButtons>
          {shareLink && <UIActionButton type="share" onClick={onShareClick} />}
          {hasAccessToBookmarks && (
            <UIActionButton
              type="bookmark"
              isActive={is_bookmarked}
              isLoading={isBookmarkPending}
              onClick={onBookmarkHandle}
            />
          )}
        </StyledButtons>
      </MasterDetailContentWrapper>
      {hasAccessToMonitor ? (
        <>
          <MasterDetailContentWrapper>
            <SectionTitleUnderlined>{translate("app.topic.related_topics")}</SectionTitleUnderlined>
            <TopicSlider
              hideFollowAction
              numberOfItems={{ small: 1, medium: 3, large: 2, extraLarge: 3, enormous: 4 }}
              topicBack={topicBack}
              topics={related_topics}
            />
          </MasterDetailContentWrapper>
          <StyledDigDeeperSection>
            <SectionTitleUnderlined>{translate("app.cluster.dig_deeper")}</SectionTitleUnderlined>
            <StyledDigDeeperList>
              <DigDeeperList clusterId={cluster.id} />
            </StyledDigDeeperList>
          </StyledDigDeeperSection>
        </>
      ) : (
        <MasterDetailContentWrapper>
          <JoinUsUpgradeBanner trackPage="Cluster detail" />
        </MasterDetailContentWrapper>
      )}
    </>
  );
};
